import { useFaceDetections } from '@src/hooks/useFaceDetections';
import {
  type SceneData,
  type SceneDetectionResponse,
  SceneDetectionService,
} from '@src/services/framing/SceneDetectionService';
import { useEditor } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import RefreshIcon from '@src/svgs/RefreshIcon';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import PrimaryActionButton from './PrimaryActionButton';

import type { Story } from '../../types.ts/story';

type Props = {
  story?: Story;
  onNavigateToFraming?: () => void;
};

type DetectorType = string;

// Re-export for backwards compatibility
export type SceneDetection = SceneDetectionResponse;

// Map of detector types to their parameter fields
type ParamField = {
  name: string;
  label: string;
  type: 'number' | 'weights';
  step?: string;
  defaultValue?: number;
};

const DETECTOR_PARAMS: Record<DetectorType, ParamField[]> = {
  content: [
    {
      name: 'threshold',
      label: 'Threshold',
      type: 'number',
      step: '0.1',
      defaultValue: 27.0,
    },
    {
      name: 'min_scene_len',
      label: 'Min Scene Length (frames)',
      type: 'number',
      step: '1',
      defaultValue: 15,
    },
    { name: 'weights', label: 'Weights', type: 'weights' },
  ],
  adaptive: [
    {
      name: 'adaptive_threshold',
      label: 'Adaptive Threshold',
      type: 'number',
      step: '0.1',
      defaultValue: 3.0,
    },
    {
      name: 'min_scene_len',
      label: 'Min Scene Length (frames)',
      type: 'number',
      step: '1',
      defaultValue: 15,
    },
    {
      name: 'window_width',
      label: 'Window Width',
      type: 'number',
      step: '1',
      defaultValue: 2,
    },
    {
      name: 'min_content_val',
      label: 'Min Content Val',
      type: 'number',
      step: '0.1',
      defaultValue: 15.0,
    },
    { name: 'weights', label: 'Weights', type: 'weights' },
  ],
  hash: [
    {
      name: 'threshold',
      label: 'Threshold',
      type: 'number',
      step: '0.001',
      defaultValue: 0.395,
    },
    {
      name: 'size',
      label: 'Size',
      type: 'number',
      step: '1',
      defaultValue: 16,
    },
    {
      name: 'lowpass',
      label: 'Lowpass',
      type: 'number',
      step: '1',
      defaultValue: 2,
    },
    {
      name: 'min_scene_len',
      label: 'Min Scene Length (frames)',
      type: 'number',
      step: '1',
      defaultValue: 15,
    },
  ],
  histogram: [
    {
      name: 'threshold',
      label: 'Threshold',
      type: 'number',
      step: '0.01',
      defaultValue: 0.05,
    },
    {
      name: 'bins',
      label: 'Bins',
      type: 'number',
      step: '1',
      defaultValue: 256,
    },
    {
      name: 'min_scene_len',
      label: 'Min Scene Length (frames)',
      type: 'number',
      step: '1',
      defaultValue: 30,
    },
  ],
  threshold: [
    {
      name: 'threshold',
      label: 'Threshold',
      type: 'number',
      step: '0.1',
      defaultValue: 27.0,
    },
    {
      name: 'min_scene_len',
      label: 'Min Scene Length (frames)',
      type: 'number',
      step: '1',
      defaultValue: 15,
    },
  ],
};

const DEFAULT_WEIGHTS = {
  delta_hue: 1.0,
  delta_sat: 1.0,
  delta_lum: 1.0,
  delta_edges: 0.0,
};

const AISceneProducer = observer((props: Props) => {
  const videoCreator = useVideoCreatorStore();
  const editor = useEditor().editor;
  const [detections, setDetections] = useState<SceneDetection[]>([]);
  const [loading, setLoading] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const [selectedDetector, setSelectedDetector] =
    useState<DetectorType>('histogram');
  const [params, setParams] = useState<
    Record<string, number | Record<string, number>>
  >({});
  const [showWeights, setShowWeights] = useState(false);
  const [detecting, setDetecting] = useState(false);
  const pollingIntervalRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const storyId = props.story?.id || videoCreator.story?.id;
  const { createFaceDetections, detecting: faceDetecting } =
    useFaceDetections(storyId);

  const handleDetectFaces = async (sceneDetectId: string) => {
    await createFaceDetections(sceneDetectId);
    // Navigate to Framing tab after initiating face detection
    if (props.onNavigateToFraming) {
      props.onNavigateToFraming();
    }
  };

  useEffect(() => {
    if (storyId) {
      fetchSceneDetections();
    }
  }, [storyId]);

  useEffect(() => {
    // Cleanup polling on unmount
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, []);

  // Use SceneDetectionService for all API operations
  const sceneDetectionService = useMemo(() => new SceneDetectionService(), []);

  const fetchSceneDetections = useCallback(async () => {
    if (!storyId) return;

    setLoading(true);
    try {
      const result = await sceneDetectionService.fetchSceneDetections(storyId);
      if (result.ok) {
        setDetections(result.value);

        // Stop polling if first detection has scenes defined
        if (
          result.value.length > 0 &&
          result.value[0].scenes &&
          pollingIntervalRef.current
        ) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
          setDetecting(false);
        }
      }
    } catch (error) {
      console.error('Failed to fetch scene detections:', error);
    } finally {
      setLoading(false);
    }
  }, [storyId, sceneDetectionService]);

  const handleDetectScenes = async () => {
    if (!storyId) return;

    setLoading(true);
    setDetecting(true);
    try {
      const result = await sceneDetectionService.createSceneDetection(storyId, {
        detector_type: selectedDetector,
        ...params,
      });

      if (result.ok) {
        console.log('Scene detection started:', result.value);

        // Start polling for results every 5 seconds
        const interval = setInterval(() => {
          fetchSceneDetections();
        }, 5000);
        pollingIntervalRef.current = interval;
      }
    } catch (error) {
      console.error('Failed to start scene detection:', error);
      setDetecting(false);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDetection = async (sceneDetectId: string) => {
    if (!storyId) return;

    try {
      const result = await sceneDetectionService.deleteSceneDetection(
        storyId,
        sceneDetectId,
      );

      if (result.ok) {
        setDetections(prev =>
          prev.filter(d => d.sceneDetectId !== sceneDetectId),
        );
      }
    } catch (error) {
      console.error('Failed to delete scene detection:', error);
    }
  };

  const parseSceneCSV = async (csvUrl: string): Promise<SceneData[]> => {
    const result = await sceneDetectionService.parseSceneCSV(csvUrl);
    if (!result.ok) {
      console.error('Failed to parse scene CSV:', result.error.message);
      return [];
    }
    return result.value;
  };

  const handleApplyCuts = async (detection: SceneDetection) => {
    if (!detection.scenes) {
      console.error('No scenes URL available');
      return;
    }

    try {
      setLoading(true);
      const scenes = await parseSceneCSV(detection.scenes);

      if (scenes.length === 0) {
        console.error('No scenes parsed from CSV');
        return;
      }

      if (!editor) {
        console.error('No video editor available');
        return;
      }

      // Build ranges to keep (crop to these ranges)
      const ranges: { start: number; end: number }[] = scenes.map(scene => ({
        start: scene.startTime,
        end: scene.endTime,
      }));

      // Apply the crop command with scene ranges
      editor.commandCropTimeline(ranges);

      console.log(`Applied ${scenes.length} scene cuts to timeline`);
    } catch (error) {
      console.error('Failed to apply cuts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateScenes = async (detection: SceneDetection) => {
    if (!detection.scenes) {
      console.error('No scenes URL available');
      return;
    }

    if (!editor) {
      console.error('No video editor available');
      return;
    }

    try {
      setLoading(true);
      const naturalMediaDimensions =
        await videoCreator.getNaturalMediaDimensions();
      const result =
        await sceneDetectionService.createScenesFromDetectionResponse(
          detection,
          editor,
          naturalMediaDimensions,
        );

      if (result.ok) {
        console.log('Created scenes based on media time');
      } else {
        console.error('Failed to create scenes:', result.error.message);
      }
    } catch (error) {
      console.error('Failed to create scenes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleParamChange = (name: string, value: string) => {
    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      setParams(prev => ({ ...prev, [name]: numValue }));
    }
  };

  const handleWeightChange = (weightName: string, value: string) => {
    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      setParams(prev => ({
        ...prev,
        weights: {
          ...(typeof prev.weights === 'object'
            ? prev.weights
            : DEFAULT_WEIGHTS),
          [weightName]: numValue,
        },
      }));
    }
  };

  const currentDetectorParams = DETECTOR_PARAMS[selectedDetector] || [];
  const currentWeights =
    typeof params.weights === 'object' ? params.weights : DEFAULT_WEIGHTS;

  return (
    <Main>
      <DetectSection>
        <PrimaryActionButton
          onClick={handleDetectScenes}
          disabled={loading || detecting}
        >
          {detecting ? 'Detecting Scenes...' : 'Detect Scenes'}
        </PrimaryActionButton>

        <OptionsToggle onClick={() => setShowOptions(!showOptions)}>
          {showOptions ? '▼' : '▶'} Options
        </OptionsToggle>

        {showOptions && (
          <OptionsPanel>
            <OptionRow>
              <Label>Detector Type</Label>
              <Select
                value={selectedDetector}
                onChange={e => {
                  setSelectedDetector(e.target.value);
                  setParams({});
                }}
              >
                {Object.keys(DETECTOR_PARAMS).map(type => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </Select>
            </OptionRow>

            {currentDetectorParams.map(param => {
              if (param.type === 'weights') {
                return (
                  <WeightsSection key={param.name}>
                    <WeightsToggle onClick={() => setShowWeights(!showWeights)}>
                      {showWeights ? '▼' : '▶'} {param.label}
                    </WeightsToggle>
                    {showWeights && (
                      <WeightsPanel>
                        {(
                          Object.keys(DEFAULT_WEIGHTS) as Array<
                            keyof typeof DEFAULT_WEIGHTS
                          >
                        ).map(weightName => (
                          <OptionRow key={weightName}>
                            <Label>{weightName}</Label>
                            <Input
                              type="number"
                              step="0.1"
                              value={
                                currentWeights[weightName] ??
                                DEFAULT_WEIGHTS[weightName]
                              }
                              onChange={e =>
                                handleWeightChange(weightName, e.target.value)
                              }
                              placeholder={String(DEFAULT_WEIGHTS[weightName])}
                            />
                          </OptionRow>
                        ))}
                      </WeightsPanel>
                    )}
                  </WeightsSection>
                );
              }
              const paramValue = params[param.name];
              return (
                <OptionRow key={param.name}>
                  <Label>{param.label}</Label>
                  <Input
                    type="number"
                    step={param.step || '0.01'}
                    value={typeof paramValue === 'number' ? paramValue : ''}
                    onChange={e =>
                      handleParamChange(param.name, e.target.value)
                    }
                    placeholder={
                      param.defaultValue
                        ? String(param.defaultValue)
                        : 'Default'
                    }
                  />
                </OptionRow>
              );
            })}
          </OptionsPanel>
        )}
      </DetectSection>

      <DetectionsList>
        <DetectionsHeader>
          <HeaderText>Scene Detections</HeaderText>
          <RefreshButton
            onClick={fetchSceneDetections}
            disabled={loading}
            title="Refresh scene detections"
          >
            <RefreshIcon
              width="14"
              height="14"
              strokeColor="var(--text-muted)"
            />
          </RefreshButton>
        </DetectionsHeader>

        {loading && <LoadingText>Loading...</LoadingText>}

        {!loading && detections.length === 0 && (
          <EmptyState>No scene detections yet</EmptyState>
        )}

        {detections.map((detection, index) => (
          <DetectionItem key={detection.sceneDetectId}>
            <DetectionHeader>
              <DetectorLabel title={detection.input.detector_type}>
                Scene Detection #{index + 1}
              </DetectorLabel>
              <ParamsText
                title={JSON.stringify(detection.input.params, null, 2)}
              >
                {detection.input.detector_type}
              </ParamsText>
            </DetectionHeader>

            <ActionsRow>
              <ActionButton
                onClick={() => handleApplyCuts(detection)}
                disabled={!detection.scenes}
              >
                Apply Cuts
              </ActionButton>
              <ActionButton
                onClick={() => handleCreateScenes(detection)}
                disabled={!detection.scenes}
              >
                Create Scenes
              </ActionButton>
              {/* <ActionButton
                onClick={() => handleAddAsTrack(detection)}
                disabled={!detection.scenes}
              >
                Add as Track
              </ActionButton> */}
              <ActionButton
                onClick={() => handleDetectFaces(detection.sceneDetectId)}
                disabled={!detection.scenes || faceDetecting}
              >
                {faceDetecting ? 'Finding Faces...' : 'Find Faces'}
              </ActionButton>
              {/* {detection.stats && (
                <ActionButton
                  as="a"
                  href={detection.stats}
                  download={`scene-stats-${detection.sceneDetectId}.csv`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Download Stats
                </ActionButton>
              )} */}
              <ActionButton
                onClick={() => handleDeleteDetection(detection.sceneDetectId)}
                $danger
              >
                Delete
              </ActionButton>
            </ActionsRow>
          </DetectionItem>
        ))}
      </DetectionsList>
    </Main>
  );
});

export default AISceneProducer;

const Main = styled.div`
  padding: 8px 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const DetectSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const OptionsToggle = styled.button`
  background: none;
  border: none;
  color: var(--text-muted);
  cursor: pointer;
  font-size: var(--text-xs);
  font-weight: 500;
  text-align: left;
  padding: 4px 0;

  &:hover {
    color: var(--foreground);
  }
`;

const WeightsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const WeightsToggle = styled.button`
  background: none;
  border: none;
  color: var(--text-muted);
  cursor: pointer;
  font-size: var(--text-xs);
  font-weight: 500;
  text-align: left;
  padding: 4px 0;

  &:hover {
    color: var(--foreground);
  }
`;

const WeightsPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: var(--spacing-sm);
  background: var(--background);
  border-radius: 8px;
  border: 1px solid var(--border);
`;

const OptionsPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
  padding: 10px 12px;
  background: var(--surface);
  border-radius: 8px;
  border: 1px solid var(--border);
`;

const OptionRow = styled.div`
  display: grid;
  grid-template-columns: 120px 1fr;
  align-items: center;
  gap: 10px;
`;

const Label = styled.label`
  font-size: var(--text-xs);
  color: var(--text-muted);
`;

const Select = styled.select`
  background: var(--background);
  border: 1px solid var(--border);
  border-radius: 8px;
  color: var(--foreground);
  padding: 6px 8px;
  font-size: var(--text-xs);

  &:focus {
    outline: none;
    border-color: var(--foreground);
  }
`;

const Input = styled.input`
  background: var(--background);
  border: 1px solid var(--border);
  border-radius: 8px;
  color: var(--foreground);
  padding: 6px 8px;
  font-size: var(--text-xs);

  &:focus {
    outline: none;
    border-color: var(--foreground);
  }

  &::placeholder {
    color: var(--text-muted);
  }
`;

const DetectionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const DetectionsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const HeaderText = styled.div`
  font-size: var(--text-sm);
  font-weight: var(--font-weight-semi-bold);
  color: var(--foreground);
`;

const RefreshButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;

  &:hover {
    background: var(--surface);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const LoadingText = styled.div`
  color: var(--text-muted);
  font-size: var(--text-xs);
  text-align: center;
  padding: var(--spacing-md);
`;

const EmptyState = styled.div`
  color: var(--text-muted);
  font-size: var(--text-xs);
  text-align: center;
  padding: var(--spacing-md);
`;

const DetectionItem = styled.div`
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const DetectionHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const DetectorLabel = styled.div`
  font-size: var(--text-sm);
  font-weight: var(--font-weight-semi-bold);
  color: var(--foreground);
`;

const ParamsText = styled.div`
  font-size: var(--text-xs);
  color: var(--text-muted);
`;

const ActionsRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const ActionButton = styled.button<{ $danger?: boolean }>`
  background: transparent;
  border: 1px solid
    ${props => (props.$danger ? 'var(--destructive)' : 'var(--border)')};
  border-radius: 8px;
  color: ${props =>
    props.$danger ? 'var(--destructive)' : 'var(--foreground)'};
  cursor: pointer;
  font-size: var(--text-xs);
  font-weight: 500;
  padding: 6px 10px;
  transition: all 0.15s ease;

  &:hover:not(:disabled) {
    border-color: ${props =>
      props.$danger ? 'var(--destructive)' : 'var(--foreground)'};
  }

  &:active:not(:disabled) {
    background: var(--surface);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
